import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import gql from 'graphql-tag';
import xor from 'lodash/xor';
import Button from '@components/buttons';
import Divider from '@components/Divider';
import Checkbox from '@components/form/checkbox/Checkbox';
import Label from '@components/form/Label';
import SwitchLabel from '@components/form/SwitchLabel';
import { Box, Flex } from '@components/layout/Grid';
import { H4 } from '@components/typography/Headings';
import { P } from '@components/typography/Paragraph';
import { Footer, Header, Body, Modal } from './base';
import { videoSettingsModalOpenVar } from '@lib/apollo';

const TERMS = () => {
  return [
    {
      value: 'prenatal',
      display_name: t`settings_modal.prenatal`,
    },
    {
      value: 'postnatal',
      display_name: t`settings_modal.postnatal`,
    },
    {
      value: 'senioryoga',
      display_name: t`settings_modal.senior-yoga`,
    },
    {
      value: 'barn-ungdom',
      display_name: t`settings_modal.barn-ungdom`,
    },
    {
      value: 'accessible-yoga',
      display_name: t`settings_modal.accessible-yoga`,
    },
    {
      value: 'office-yoga',
      display_name: t`settings_modal.office-yoga`,
    },
  ];
};

const LANGUAGES = () => {
  return [
    {
      label: t`settings_modal.swedish`,
      slug: 'se',
      icon: 'ic_flag_sweden',
    },
    {
      label: t`settings_modal.norwegian`,
      slug: 'no',
      icon: 'ic_flag_norway',
    },
    {
      label: t`settings_modal.english`,
      slug: 'en',
      icon: 'ic_flag_greatbritain',
    },
    {
      label: t`settings_modal.finnish`,
      slug: 'fi',
      icon: 'ic_flag_finland',
    },
  ];
};

const H4EXT = styled(H4)`
  margin: 0;
`;

const Img = styled.img`
  display: inline-block;
`;

const SETTINGS_MODAL_QUERY = gql`
  query isVideoSettingsModalOpen {
    isVideoSettingsModalOpen @client
  }
`;

const SettingsModal = props => {
  const { loading: loadingModalState, data } = useQuery(SETTINGS_MODAL_QUERY);
  const { isVideoSettingsModalOpen: open } = data || {};

  const [excludeTerms, setExcludeTerms] = useState([]);
  const [contentLanguages, setContentLanguages] = useState([]);
  const [videoLevels, setVideoLevels] = useState([]);

  React.useEffect(() => {
    const { videoSettings } = props;
    setExcludeTerms(videoSettings.excludeTerms);
    setContentLanguages(videoSettings.contentLanguages);
    setVideoLevels(videoSettings.videoLevels);
  }, [props.videoSettings]);

  if (loadingModalState) return null;

  const isExcluded = term => {
    return !excludeTerms.includes(term.value);
  };

  const isLanguageChosen = lang => {
    return contentLanguages.includes(lang);
  };

  const toggleLanguage = lang => {
    if (isLanguageChosen(lang) && contentLanguages.length === 1) {
      //do nothing
    } else {
      setContentLanguages(xor(contentLanguages, [lang]));
    }
  };

  const toggleBeginnerFilter = () => {
    if (videoLevels?.length > 0) {
      setVideoLevels([]);
    } else {
      setVideoLevels(['beginner']);
    }
  };

  const toggleTerm = term => {
    setExcludeTerms(xor(excludeTerms, [term.value]));
  };

  const handleClose = () => {
    videoSettingsModalOpenVar(false);
  };

  const handleSave = () => {
    props.handleSave({ excludeTerms, contentLanguages, videoLevels });
    handleClose();
  };

  return (
    <Modal open={open} handleClose={handleClose} customWidth="650px">
      <Header handleClose={handleClose}>
        <H4EXT>
          <Trans id="settings_modal.title" message={'Settings'} />
        </H4EXT>
      </Header>
      <Body pt={20} px={[25, 25, 50]} pb={30}>
        <Box mb="10px">
          <Label>
            <Trans
              id="settings_modal.left_section_title"
              message={'Languages'}
            />
          </Label>
        </Box>
        <Flex mb="10px" flexWrap="wrap">
          {LANGUAGES().map(lang => (
            <Box key={lang.slug} mr="20px">
              <Checkbox
                isChecked={isLanguageChosen(lang.slug)}
                onChange={() => {
                  toggleLanguage(lang.slug);
                }}
              >
                <Img src={`/static/flags/${lang.icon}@2x.png`} />
                {` ${lang.label}`}
              </Checkbox>
            </Box>
          ))}
        </Flex>
        <Box as={Divider} mb="20px" />
        <Box mb="10px">
          <Label>
            <Trans
              id="settings_modal.right_section_title"
              message={'Sökfilter'}
            />
          </Label>
        </Box>
        <Box mb="10px">
          <Checkbox
            onChange={() => toggleBeginnerFilter()}
            isChecked={videoLevels?.length > 0}
          >
            <Trans
              id="settings_modal.beginner"
              message={'Beginner friendly videos'}
            />
          </Checkbox>
        </Box>
        <Box as={Divider} mb="20px" />
        <Box mb="20px">
          <Box mb="10px">
            <Label>
              <Trans
                id="settings_modal.helper_label"
                message={'Customize your Yogobe experience'}
              />
            </Label>
          </Box>
          <P>
            <Trans
              id="settings_modal.helper"
              message={
                'Get more relevant results by selecting what is included in your search results.'
              }
            />
          </P>
        </Box>
        <Flex flexWrap="wrap" mx={['0px', '0px', '-50px']}>
          {TERMS().map((term, index) => (
            <Box width={[1, 1, 1 / 2]} px={['0px', '0px', '50px']} key={index}>
              <SwitchLabel
                label={term.display_name}
                value={isExcluded(term)}
                onChange={() => {
                  toggleTerm(term);
                }}
              />
            </Box>
          ))}
        </Flex>
      </Body>
      <Footer>
        <Button appearance="link" onClick={handleClose}>
          <Trans id="settings_modal.secondary_button" message={'AVBRYT'} />
        </Button>
        <Button loading={props.loading} onClick={handleSave}>
          <Trans id="settings_modal.primary_button" message={'SKAPA'} />
        </Button>
      </Footer>
    </Modal>
  );
};

export default SettingsModal;
